import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../firebase/firebaseConfig';
import { ref, query, orderByChild, equalTo, get, update, increment } from 'firebase/database';
import { useAuth } from '../context/AuthContext';
import StatsCard from '../components/StatsCard';
import './PublicProfile.css';

function PublicProfile() {
  const { profileId } = useParams();
  const { currentUser, userProfile: visitorProfile } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [scanMessage, setScanMessage] = useState("");

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    const fetchProfile = async () => {
      // 0. Handle Demo Mode (Showcase)
      if (profileId === 'demo') {
        setProfile({
          name: "DevTracker Showcase",
          profileId: "demo",
          bio: "This is a live demonstration profile. DevTracker consolidates your coding identity across multiple platforms into one beautiful, real-time dashboard. Create your own profile to start tracking!",
          githubUsername: "gaearon", // Dan Abramov - very active
          leetcodeUsername: "terry_6", // Random active user
          codeforcesUsername: "tourist", // The legend
          gfgUsername: "vivek",
          isDemo: true
        });
        setLoading(false);
        return;
      }

      try {
        // 1. Check Maintenance Mode
        const settingsSnap = await get(ref(db, 'settings/maintenanceMode'));
        if (settingsSnap.exists() && settingsSnap.val() === true) {
          setError("Platform is currently undergoing maintenance. Profile rendering is disabled.");
          setLoading(false);
          return;
        }

        // 2. Fetch User
        const usersRef = ref(db, 'users');
        const q = query(usersRef, orderByChild('profileId'), equalTo(profileId));
        const snapshot = await get(q);
        
        if (!snapshot.exists()) {
          setError("Developer profile not found.");
        } else {
          // snapshot.val() returns an object with userUID keys. We just grab the first matching one
          const data = snapshot.val();
          const firstKey = Object.keys(data)[0];
          const userData = data[firstKey];
          
          if (userData.settings?.profileVisibility === 'private') {
            setError("This developer profile is currently set to Private and cannot be viewed.");
          } else if (userData.status === 'suspended' || userData.status === 'banned' || userData.status === 'blocked') {
            setError("This developer profile is currently restricted due to a platform policy violation.");
          } else {
            setProfile(userData);
          }
        }
      } catch (err) {
        console.error(err);
        setError("Error loading profile.");
      }
      setLoading(false);
    };

    fetchProfile();
  }, [profileId]);

  // --- SCAN TO EARN LOGIC ---
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('scan') === 'true' && profile && currentUser && visitorProfile) {
      const handleScanReward = async () => {
        // Prevent rewarding the owner
        if (currentUser.uid === profile.uid) return;

        // Prevent double reward (Anti-Spam)
        const scannedPath = `users/${currentUser.uid}/scannedProfiles/${profileId}`;
        const scanCheck = await get(ref(db, scannedPath));
        if (scanCheck.exists()) return;

        try {
          // Award 10 points to visitor
          await update(ref(db, `users/${currentUser.uid}`), {
            points: increment(10),
            [`scannedProfiles/${profileId}`]: Date.now()
          });
          setScanMessage("🎉 Scan-to-Earn: +10 AI Credits awarded!");
          setTimeout(() => setScanMessage(""), 5000);
        } catch (e) {
          console.error("Scan reward error", e);
        }
      };
      handleScanReward();
    }
  }, [profile, currentUser, visitorProfile, profileId]);

  if (loading) return <div className="page-container"><div className="loader" style={{margin:'100px auto'}}></div></div>;
  if (error) return <div className="page-container"><div className="error-text" style={{textAlign:'center', marginTop:'100px', fontSize:'1.2rem'}}>{error}</div></div>;

  return (
    <div className="container animate-up" style={{paddingTop: '40px'}}>
      
      {scanMessage && (
        <div className="scan-reward-toast" style={{
          position: 'fixed', 
          top: '20px', 
          left: '50%', 
          transform: 'translateX(-50%)', 
          background: 'var(--brand-accent)', 
          color: '#000', 
          padding: '12px 25px', 
          borderRadius: '30px', 
          fontWeight: 'bold', 
          zIndex: 9999, 
          boxShadow: '0 10px 30px rgba(0, 255, 174, 0.4)',
          animation: 'bounceIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
        }}>
          <i className="fa-solid fa-gift"></i> {scanMessage}
        </div>
      )}
      
      <div className="glass-card profile-bio-header">
        {profile.avatar ? (
          <img src={profile.avatar} alt="Avatar" className="profile-large-avatar" />
        ) : (
          <div className="profile-large-avatar-placeholder"><i className="fa-solid fa-user-astronaut"></i></div>
        )}
        <div className="profile-bio-text">
          <div style={{display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap', marginBottom: '10px'}}>
            <h1 style={{margin: 0}}>{profile.name}</h1>
            {profile.isDemo && (
              <span className="demo-badge">
                <i className="fa-solid fa-wand-magic-sparkles"></i> SHOWCASE MODE
              </span>
            )}
          </div>
          <p className="profile-id-txt">{profile.profileId}</p>
          <div className="bio-content">
            {profile.bio ? profile.bio : "Open-source contributor & competitive programmer."}
          </div>
          <button onClick={handleCopyLink} className="btn btn-secondary" style={{marginTop: '20px', padding: '8px 16px', fontSize: '0.9rem'}}>
            <i className={`fa-solid ${copied ? 'fa-check' : 'fa-link'}`}></i> {copied ? 'Link Copied!' : 'Copy Profile Link'}
          </button>
        </div>
      </div>

      <h2 style={{marginTop: '40px', marginBottom: '20px', textAlign: 'center'}}>Live Coding Statistics</h2>
      <div className="stats-grid public-stats-grid">
        <StatsCard platform="GitHub" username={profile.githubUsername} accentColor="var(--brand-github)" iconClass="fa-brands fa-github" />
        <StatsCard platform="LeetCode" username={profile.leetcodeUsername} accentColor="var(--brand-leetcode)" iconClass="fa-solid fa-code" />
        <StatsCard platform="Codeforces" username={profile.codeforcesUsername} accentColor="var(--brand-codeforces)" iconClass="fa-solid fa-chart-simple" />
        <StatsCard platform="GeeksforGeeks" username={profile.gfgUsername} accentColor="var(--brand-gfg)" iconClass="fa-solid fa-laptop-code" />
      </div>
      
    </div>
  );
}

export default PublicProfile;
